export const ACCOUNT_MENU_INTENT_STORAGE_KEY =
  "lettucetalk.accountMenuIntent";
export const ACCOUNT_SESSION_RECOVERY_STORAGE_KEY =
  "lettucetalk.accountSessionRecovery";

export const ACCOUNT_SESSION_RECOVERY_TTL_MS = 30 * 60 * 1000;

const STRING_LIMITS = Object.freeze({
  sourceUid: 200,
  destinationUid: 200,
  generationId: 200,
  ticketId: 300,
  returnTo: 1000,
  rawThought: 4000,
  translationLanguage: 100,
});

const VALID_TICKET_STATUSES = new Set([
  "pending",
  "redeemed",
  "expired",
]);

function getStorage() {
  try {
    if (typeof window === "undefined") {
      return null;
    }

    return window.sessionStorage || null;
  } catch {
    return null;
  }
}

function cleanString(value, maxLength) {
  if (typeof value !== "string" || !value.trim()) {
    return "";
  }

  return value.trim().slice(0, maxLength);
}

function sanitizeReturnTo(value) {
  const returnTo = cleanString(value, STRING_LIMITS.returnTo);

  if (!returnTo || !returnTo.startsWith("/") || returnTo.startsWith("//")) {
    return "";
  }

  return returnTo;
}

function sanitizeDraft(draft) {
  if (!draft || typeof draft !== "object" || Array.isArray(draft)) {
    return undefined;
  }

  const sanitizedDraft = {};
  const rawThought = cleanString(draft.rawThought, STRING_LIMITS.rawThought);
  const translationLanguage = cleanString(
    draft.translationLanguage,
    STRING_LIMITS.translationLanguage
  );

  if (rawThought) {
    sanitizedDraft.rawThought = rawThought;
  }

  if (translationLanguage) {
    sanitizedDraft.translationLanguage = translationLanguage;
  }

  return Object.keys(sanitizedDraft).length > 0 ? sanitizedDraft : undefined;
}

function sanitizeIntent(intent, { requireCreatedAt = false } = {}) {
  if (!intent || typeof intent !== "object" || Array.isArray(intent)) {
    return null;
  }

  const sourceUid = cleanString(intent.sourceUid, STRING_LIMITS.sourceUid);
  const returnTo = sanitizeReturnTo(intent.returnTo);

  if (!sourceUid || !returnTo) {
    return null;
  }

  const createdAt = Number.isFinite(intent.createdAt)
    ? intent.createdAt
    : requireCreatedAt
      ? 0
      : Date.now();

  if (!Number.isFinite(createdAt) || createdAt <= 0) {
    return null;
  }

  const sanitizedIntent = {
    action: "save-session",
    sourceUid,
    returnTo,
    createdAt,
  };
  const draft = sanitizeDraft(intent.draft);

  if (draft) {
    sanitizedIntent.draft = draft;
  }

  return sanitizedIntent;
}

function sanitizeTicket(ticket) {
  if (!ticket || typeof ticket !== "object" || Array.isArray(ticket)) {
    return null;
  }

  const ticketId = cleanString(ticket.ticketId, STRING_LIMITS.ticketId);
  const generationId = cleanString(
    ticket.generationId,
    STRING_LIMITS.generationId
  );
  const status = cleanString(ticket.status, 20);

  if (!ticketId || !generationId || !VALID_TICKET_STATUSES.has(status)) {
    return null;
  }

  return {
    ticketId,
    generationId,
    status,
  };
}

export function sanitizeAccountSessionRecoveryState(state) {
  if (!state || typeof state !== "object" || Array.isArray(state)) {
    return null;
  }

  const sourceUid = cleanString(state.sourceUid, STRING_LIMITS.sourceUid);
  const expiresAt = Number.isFinite(state.expiresAt) ? state.expiresAt : 0;
  const tickets = Array.isArray(state.tickets)
    ? state.tickets.map(sanitizeTicket).filter(Boolean)
    : [];

  if (!sourceUid || !expiresAt || tickets.length === 0) {
    return null;
  }

  const sanitizedState = {
    version: 1,
    sourceUid,
    expiresAt,
    tickets,
    createdAt: Number.isFinite(state.createdAt) ? state.createdAt : Date.now(),
  };
  const returnTo = sanitizeReturnTo(state.returnTo);
  const draft = sanitizeDraft(state.draft);
  const destinationUid = cleanString(
    state.destinationUid,
    STRING_LIMITS.destinationUid
  );

  if (destinationUid) {
    sanitizedState.destinationUid = destinationUid;
  }

  if (typeof state.destinationUidVerified === "boolean") {
    sanitizedState.destinationUidVerified = state.destinationUidVerified;
  }

  if (returnTo) {
    sanitizedState.returnTo = returnTo;
  }

  if (draft) {
    sanitizedState.draft = draft;
  }

  return sanitizedState;
}

export function saveAccountMenuIntent(intent) {
  const storage = getStorage();
  const sanitizedIntent = sanitizeIntent(intent);

  if (!storage || !sanitizedIntent) {
    return null;
  }

  try {
    storage.setItem(
      ACCOUNT_MENU_INTENT_STORAGE_KEY,
      JSON.stringify(sanitizedIntent)
    );
    return sanitizedIntent;
  } catch {
    return null;
  }
}

export function getAccountMenuIntent() {
  const storage = getStorage();

  if (!storage) {
    return null;
  }

  try {
    const storedIntent = storage.getItem(ACCOUNT_MENU_INTENT_STORAGE_KEY);
    const intent = storedIntent
      ? sanitizeIntent(JSON.parse(storedIntent), { requireCreatedAt: true })
      : null;

    if (!intent || isAccountMenuIntentExpired(intent)) {
      clearAccountMenuIntent();
      return null;
    }

    return intent;
  } catch {
    return null;
  }
}

export function isAccountMenuIntentExpired(
  intent,
  now = Date.now()
) {
  if (!Number.isFinite(intent?.createdAt) || intent.createdAt <= 0) {
    return true;
  }

  return now - intent.createdAt > ACCOUNT_SESSION_RECOVERY_TTL_MS;
}

export function clearAccountMenuIntent() {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  try {
    storage.removeItem(ACCOUNT_MENU_INTENT_STORAGE_KEY);
  } catch {
    // Session storage is best effort; callers still keep the current session.
  }
}

export function saveAccountSessionRecoveryState(state) {
  const storage = getStorage();
  const sanitizedState = sanitizeAccountSessionRecoveryState(state);

  if (!storage || !sanitizedState) {
    return false;
  }

  try {
    storage.setItem(
      ACCOUNT_SESSION_RECOVERY_STORAGE_KEY,
      JSON.stringify(sanitizedState)
    );
    return true;
  } catch {
    return false;
  }
}

export function getAccountSessionRecoveryState() {
  const storage = getStorage();

  if (!storage) {
    return null;
  }

  try {
    const storedState = storage.getItem(ACCOUNT_SESSION_RECOVERY_STORAGE_KEY);
    return storedState
      ? sanitizeAccountSessionRecoveryState(JSON.parse(storedState))
      : null;
  } catch {
    return null;
  }
}

export function clearAccountSessionRecoveryState() {
  const storage = getStorage();

  if (!storage) {
    return;
  }

  try {
    storage.removeItem(ACCOUNT_SESSION_RECOVERY_STORAGE_KEY);
  } catch {
    // Do not turn cleanup problems into a second auth failure.
  }
}

export function hasUnfinishedAccountSessionRecovery(state) {
  return Boolean(
    state?.tickets?.some((ticket) => ticket.status !== "redeemed")
  );
}

export function isAccountSessionRecoveryExpired(state, now = Date.now()) {
  return Boolean(state?.expiresAt && now >= state.expiresAt);
}

export function updateAccountSessionRecoveryState(updater) {
  const currentState = getAccountSessionRecoveryState();
  const nextState =
    typeof updater === "function" ? updater(currentState) : updater;

  if (!nextState) {
    clearAccountSessionRecoveryState();
    return false;
  }

  return saveAccountSessionRecoveryState(nextState);
}
